import { useCallback, useEffect, useMemo, useState } from 'react';

import { useUser } from '@/allauth_auth/hooks';
import { Button } from '@/lib/base/buttons/button';

import { ErrorBoundary, QueryErrorState } from '../../../components';
import { useOverlaySubscription } from '../../record/hooks/useOverlaySubscription';
import { getEntityTypeConfig } from '../../record/registry/entityTypeRegistry';
import { transformToTimelineItems } from '../../record/timeline/collapseAssociationEvents';
import { collapseCascadeEvents } from '../../record/timeline/collapseCascadeEvents';
import { collapseTimelineEvents } from '../../record/timeline/collapseTimelineEvents';
import {
  emptyViewStateLookups,
  ViewStateLookupsProvider,
} from '../../record/timeline/context/TimelineLookupsContext';
import { RelatedThreadsTruncatedNotice } from '../../record/timeline/RelatedThreadsTruncatedNotice';
import { TimelineEventList } from '../../record/timeline/TimelineEventList';
import {
  buildClearedTimelineFilters,
  getDefaultTimelineEventTypes,
  hasActiveTimelineFilters,
} from '../../record/timeline/timelineFilterDefaults';
import { TimelineFilters } from '../../record/timeline/TimelineFilters';
import { isRenderableTimelineItem } from '../../record/timeline/timelineItemVisibility';
import type {
  EventTypeName,
  TimelineFilters as TimelineFiltersType,
} from '../../record/timeline/types';
import {
  mergeLookups,
  useTimelineViewState,
} from '../../record/timeline/useTimelineViewState';
import { CollapsibleSection } from './collapsible-section';

interface TimelineSectionProps {
  entityId: string;
  orgId: string;
  entityType?: string;
  /**
   * Pin the section to exactly these event types: the filter UI is not
   * rendered and the selection cannot be widened. This is what makes a
   * dedicated view (the automation Runs tab) different from "the timeline
   * with a default filter" — a default invites widening back into the event
   * soup the surface deliberately excludes.
   */
  lockedEventTypes?: readonly EventTypeName[];
  /**
   * Render the event list with no section chrome at all — no CollapsibleSection
   * heading, no chevron. For hosts where the surrounding UI already names the
   * content (the automation Runs TAB is labelled Runs; a heading saying Runs
   * again inside it is chrome on chrome).
   */
  chromeless?: boolean;
  /**
   * Empty-state copy. Defaults to 'No timeline events'; the Runs tab passes
   * 'No runs yet' — its emptiness means "this has never fired", not "no
   * activity of any kind".
   */
  emptyMessage?: string;
}

const INITIAL_VISIBLE_COUNT = 5;

export function TimelineSection({
  entityId,
  orgId,
  entityType = '',
  lockedEventTypes,
  chromeless = false,
  emptyMessage = 'No timeline events',
}: TimelineSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const expand = useCallback(() => setIsExpanded(true), []);

  const timelineConfig = entityType
    ? getEntityTypeConfig(entityType).timelineConfig
    : undefined;

  const timelineDefaultExpanded = entityType
    ? (getEntityTypeConfig(entityType).timelineDefaultExpanded ?? true)
    : true;

  const [filters, setFilters] = useState<TimelineFiltersType>(() => ({
    eventTypes: lockedEventTypes
      ? [...lockedEventTypes]
      : getDefaultTimelineEventTypes(
          entityType,
          timelineConfig?.defaultFilters?.eventTypes
        ),
    startDate: null,
    endDate: null,
    actorId: null,
    includeAssociated:
      timelineConfig?.defaultFilters?.includeAssociated ?? false,
  }));

  const handleFiltersChange = useCallback(
    (next: TimelineFiltersType) => setFilters(next),
    []
  );

  const defaultIncludeAssociated =
    timelineConfig?.defaultFilters?.includeAssociated ?? false;
  const handleClearFilters = useCallback(
    () => setFilters(buildClearedTimelineFilters(defaultIncludeAssociated)),
    [defaultIncludeAssociated]
  );

  const user = useUser();
  const currentUserId = user?.id?.toString();

  // Preview-projected entities have no server-side row yet — fetching
  // view-state for them 404s. Suppress the fetch and show a calm empty state
  // instead of the generic error UI.
  const overlay = useOverlaySubscription(entityId);
  const isUncreated = overlay?.kind === 'create';

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useTimelineViewState({
    entityId,
    orgId,
    entityType,
    filters,
    enabled: !isUncreated,
  });

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const rawItems = useMemo(() => {
    const flatEvents = collapseTimelineEvents(
      data?.pages.flatMap(page => page.events) ?? []
    );
    return collapseCascadeEvents(transformToTimelineItems(flatEvents));
  }, [data?.pages]);

  const lookups = useMemo(() => {
    return data?.pages ? mergeLookups(data.pages) : emptyViewStateLookups;
  }, [data?.pages]);

  // True when the backend capped this contact's related email-thread fan-out to
  // the most-recently-active threads. Surfaced as a passive notice at the bottom
  // of the section (response-level flag, identical across pages).
  const relatedThreadsTruncated = useMemo(
    () => data?.pages.some(page => page.related_threads_truncated) ?? false,
    [data?.pages]
  );

  // Drop items that would render as null (internal-access properties /
  // associations, system-seeded stage creations, incomplete association-change
  // pairs) so they don't consume INITIAL_VISIBLE_COUNT slots or falsely
  // trigger the Expand CTA. The slice below counts only what actually renders.
  // Item components keep their own `null` returns as the fail-closed safety net
  // — this filter is purely about visible-slot accounting.
  const allItems = useMemo(
    () => rawItems.filter(item => isRenderableTimelineItem(item, lookups)),
    [rawItems, lookups]
  );

  // A page can come back with events that all filter out (e.g. a burst of
  // internal property updates) while more pages remain. Without this, the
  // section would show "No timeline events" even though renderable events
  // exist further back. Auto-advance through fully-filtered pages until a
  // renderable item surfaces or the stream is exhausted (hasNextPage → false).
  // Gated on `!error` so a failed fetchNextPage doesn't loop forever — the
  // user retries explicitly via the error UI's Retry button.
  useEffect(() => {
    if (
      allItems.length === 0 &&
      hasNextPage &&
      !isFetchingNextPage &&
      !isLoading &&
      !error
    ) {
      fetchNextPage();
    }
  }, [
    allItems.length,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    fetchNextPage,
  ]);

  const visibleItems = isExpanded
    ? allItems
    : allItems.slice(0, INITIAL_VISIBLE_COUNT);
  const hasMore =
    allItems.length > INITIAL_VISIBLE_COUNT || (hasNextPage ?? false);

  let content;

  if (isUncreated) {
    content = (
      <div className='flex items-center gap-2 py-2'>
        {/* TODO(copy): this string covers two distinct future states — not
            yet approved (record will be created) and deselected from an
            already-approved set (record never will be) — that may want
            different copy. Design decision for Laura; the neutral string
            below is true in both cases. Don't write "proposal" into this
            string — it's internal ontology. */}
        <p className='text-xs md:text-sm text-[var(--color-text-tertiary)]'>
          Nothing to show until this record is created
        </p>
      </div>
    );
  } else if (isLoading) {
    content = <TimelineSkeleton />;
  } else if (error) {
    content = (
      <QueryErrorState
        error={error}
        label="We couldn't load the timeline"
        onRetry={handleRetry}
        size='sm'
      />
    );
  } else if (allItems.length === 0 && (hasNextPage || isFetchingNextPage)) {
    // No renderable items on the pages fetched so far, but more remain — the
    // auto-advance effect is fetching them. Show the skeleton rather than a
    // misleading "No timeline events" that would flash before events arrive.
    content = <TimelineSkeleton />;
  } else if (allItems.length === 0) {
    content = (
      <div className='flex items-center gap-2 py-2'>
        <p className='text-xs md:text-sm text-[var(--color-text-tertiary)]'>
          {emptyMessage}
        </p>
        {/* No clear-filters affordance when locked — the empty state means
            "this has never fired", not "your filter hid something". */}
        {!lockedEventTypes && hasActiveTimelineFilters(filters) && (
          <Button
            color='link-gray'
            size='sm'
            onClick={handleClearFilters}
            className='!text-xs md:!text-sm !text-fg-brand-primary !py-0'
          >
            Clear filters
          </Button>
        )}
      </div>
    );
  } else {
    content = (
      <ErrorBoundary errorMessage="We couldn't render the timeline">
        <ViewStateLookupsProvider
          lookups={lookups}
          currentUserId={currentUserId}
        >
          <div className='overflow-hidden [&_*]:!text-[11px] md:[&_*]:!text-xs [&_.py-4]:!py-0 [&_.text-quaternary]:!hidden'>
            <TimelineEventList
              items={visibleItems}
              entityType={entityType}
              hasNextPage={isExpanded ? (hasNextPage ?? false) : false}
              isFetchingNextPage={isFetchingNextPage}
              onLoadMore={fetchNextPage}
            />
          </div>
          {!isExpanded && hasMore && (
            <Button
              color='link-gray'
              size='sm'
              onClick={expand}
              className='!text-xs md:!text-sm !text-fg-brand-primary mt-1'
            >
              Expand ∨
            </Button>
          )}
          {/* Shown only within the expanded view (the full oldest-end of the
              section), rendered outside the list wrapper above which hides
              .text-quaternary footers. */}
          {isExpanded && relatedThreadsTruncated && (
            <RelatedThreadsTruncatedNotice compact />
          )}
        </ViewStateLookupsProvider>
      </ErrorBoundary>
    );
  }

  if (chromeless) {
    return content;
  }

  return (
    <CollapsibleSection
      title='Timeline'
      defaultExpanded={timelineDefaultExpanded}
      actions={
        lockedEventTypes ? undefined : (
          <TimelineFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            defaultIncludeAssociated={defaultIncludeAssociated}
          />
        )
      }
    >
      {content}
    </CollapsibleSection>
  );
}

const SKELETON_ROWS = [
  { textW: 'w-3/4', tsW: 'w-16' },
  { textW: 'w-2/3', tsW: 'w-14' },
  { textW: 'w-4/5', tsW: 'w-16' },
  { textW: 'w-1/2', tsW: 'w-12' },
];

function TimelineSkeletonRow({
  textW,
  tsW,
  showConnector,
}: {
  textW: string;
  tsW: string;
  showConnector: boolean;
}) {
  return (
    <div className='relative flex items-start gap-3 py-3'>
      {showConnector && (
        <div className='absolute left-[13px] top-[39px] bottom-[-12px] w-px bg-border-secondary' />
      )}
      <div className='size-[26px] rounded-full bg-bg-tertiary animate-pulse shrink-0' />
      <div className='flex-1 flex items-center justify-between gap-4 min-h-7'>
        <div
          className={`h-3.5 bg-bg-tertiary rounded animate-pulse ${textW}`}
        />
        <div
          className={`h-3 bg-bg-tertiary rounded animate-pulse shrink-0 ${tsW}`}
        />
      </div>
    </div>
  );
}

function TimelineSkeleton() {
  return (
    <div className='space-y-0'>
      {SKELETON_ROWS.map((row, i) => (
        <TimelineSkeletonRow
          key={row.textW}
          textW={row.textW}
          tsW={row.tsW}
          showConnector={i < SKELETON_ROWS.length - 1}
        />
      ))}
    </div>
  );
}
